import { useMutation, useQuery, UseQueryResult } from "@tanstack/react-query";
import { ReferenceDto, ReferenceGridDto, SearchReferenceDto, } from "../../../services/dto";
import { IActionResponse, IActionResult, IPagedResult, } from "../../../assets/utils/lib";
import { ReferenceClient } from "../../../services/proxies";
import { Constants } from "../../../assets/utils/lib/constants";
import { LookupDto } from "../../../assets/utils/lib/lookupDto";
import { FileHelper } from "../../../assets/utils/helpers/fileHelper";

export namespace ReferenceService {
    const referenceClient = new ReferenceClient();

    export const save = () =>
        useMutation<IActionResult<ReferenceDto>, Error, ReferenceDto>({
            mutationFn: async (payload: ReferenceDto) => {
                return await referenceClient.saveReferenceAsync(payload);
            },
        });

    export const getReferenceQuery = (referenceId: string | undefined) =>
        useQuery({
            queryKey: ["referenceQuery", referenceId],
            queryFn: async () => await referenceClient.getReferenceAsync(referenceId),
            enabled: Boolean(referenceId && referenceId !== Constants.emptyGuid),
            select: (data) => {
                if (data?.image?.extension && !data.image.mimeType) {
                    data.image.mimeType =
                        FileHelper.extensionToMime[data.image.extension] ?? "application/octet-stream";
                }
                if (
                    data?.backgroundImage?.extension &&
                    !data.backgroundImage.mimeType
                ) {
                    data.backgroundImage.mimeType =
                        FileHelper.extensionToMime[data.backgroundImage.extension] ??
                        "application/octet-stream";
                }
                if (data?.headerImage?.extension && !data.headerImage.mimeType) {
                    data.headerImage.mimeType =
                        FileHelper.extensionToMime[data.headerImage.extension] ??
                        "application/octet-stream";
                }
                return data;
            },
        });

    export const gridQuery = (
        searchQuery: SearchReferenceDto
    ): UseQueryResult<IPagedResult<ReferenceGridDto>> =>
        useQuery({
            queryKey: ["referenceGridQuery", searchQuery],
            queryFn: async () =>
                await referenceClient.searchReferenceAsync(searchQuery),
            enabled: Boolean(
                searchQuery.communityId &&
                searchQuery.communityId !== Constants.emptyGuid &&
                searchQuery.linkTypeId &&
                searchQuery.linkTypeId !== Constants.emptyGuid
            ),
            placeholderData: (previousData) => previousData,
        });

    export const deleteReference = () =>
        useMutation<IActionResponse, Error, string>({
            mutationFn: async (id: string | undefined) => {
                return await referenceClient.deleteReferenceAsync(id);
            },
        });

    export const getLookups = () =>
        useQuery({
            queryKey: ["referenceLookupsQuery"],
            queryFn: async (): Promise<{
                communities: LookupDto[] | undefined;
                pageLink: LookupDto[] | undefined;
            }> => await referenceClient.getLookupsAsync(),
        });

    export const getAllCommunityReferencesQuery = (
        communityId: string
    ) =>
        useQuery({
            queryKey: ["communityReferenceQuery", communityId],
            queryFn: async () =>
                await referenceClient.getAllCommunityReferencesAsync(
                    communityId
                ),
            enabled: Boolean(communityId && communityId !== Constants.emptyGuid),
        });
}